import { getSelectStyles } from '../utility/selectStyles';
import { BASE_DOMAIN, autoGenDnsPattern, isBaseDomain } from '../utility/baseDomain';
import React, { useState } from 'react';
import Modal from './Modal';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import Select from 'react-select';
import axiosInstance from "../utility/axiosInstance";
import { useToast } from "./ToastContext";

interface CreateLoadBalancerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
}

const CreateLoadBalancerModal: React.FC<CreateLoadBalancerModalProps> = ({ isOpen, onClose, onCreated }) => {
    const { showToast } = useToast();
    const instances = useSelector((state: RootState) => state.instances.data);
    const securityGroups = useSelector((state: RootState) => state.securityGroups.data);
    const networks = useSelector((state: RootState) => state.privateNetwork.networks);
    
    const [name, setName] = useState('');
    const [port, setPort] = useState(80);
    const [dnsName, setDnsName] = useState('');
    const [isDnsGenerated, setIsDnsGenerated] = useState(false);
    const [selectedInstances, setSelectedInstances] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const generateShortId = (length = 8) => {
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += chars[Math.floor(Math.random() * chars.length)];
        }
        return result;
    };

    const generateDnsName = () => {
        const base = name ? name.toLowerCase().replace(/[^a-z0-9-]/g, '-') : 'lb';
        return `${base}-${generateShortId(8)}.${BASE_DOMAIN}`;
    };

    const instanceOptions = instances.map(inst => {
        let privateIp = '';
        let subnetId = '';
        
        // Find the fixed IP and the network it belongs to
        for (const [netName, addrs] of Object.entries(inst.addresses || {})) {
            const fixed = (addrs as any[]).find((a: any) => a["OS-EXT-IPS:type"] === "fixed");
            if (fixed) {
                privateIp = fixed.addr;
                // Try to find the subnet ID from the addresses (some OpenStack versions return it)
                subnetId = fixed.subnet_id;
                
                // If not found in addresses, find the network in our networks slice
                if (!subnetId) {
                    const network = networks.find(n => n.name === netName || n.id === netName);
                    if (network) {
                        subnetId = network.subnet_id || '';
                    }
                }
                break;
            }
        }
        
        return {
            value: inst.id,
            label: `${inst.name} (${privateIp || 'No IP'})`,
            address: privateIp,
            subnetId: subnetId || inst.metadata?.subnet_id || (inst as any).subnet_id,
            securityGroupId: securityGroups.find(sg => inst.security_groups?.includes(sg.name))?.id
        };
    });

    const handleSubmit = async () => {
        if (!name) {
            showToast('Please enter a name', 'error');
            return;
        }
        if (selectedInstances.length === 0) {
            showToast('Please select at least one instance', 'error');
            return;
        }
        const fqdnPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
        if (dnsName && isBaseDomain(dnsName) && !autoGenDnsPattern.test(dnsName)) {
            showToast(`DNS name cannot end with .${BASE_DOMAIN}. Leave empty for random generation or click ⟳ Generate.`, 'error');
            return;
        }
        if (dnsName && !isBaseDomain(dnsName) && !fqdnPattern.test(dnsName)) {
            showToast('DNS name is not a valid FQDN. Only alphanumeric characters and hyphens are allowed, separated by dots.', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            const members = selectedInstances.map(inst => ({
                address: inst.address,
                protocolPort: port,
                subnetId: inst.subnetId,
                securityGroupId: inst.securityGroupId,
                instanceId: inst.value
            }));

            // Check if all members have required data
            const missingData = members.find(m => !m.address || !m.subnetId || !m.securityGroupId);
            if (missingData) {
                showToast(`Missing network or security details for ${missingData.address || 'selected instance'}. Try reloading the page.`, 'error');
                setIsSubmitting(false);
                return;
            }

            onClose();

            await axiosInstance.post('/v1/loadbalancer/create', {
                name,
                listenerPort: port,
                members,
                dnsName
            });

            showToast('Load balancer created', 'success');
            setName('');
            setPort(80);
            setDnsName('');
            setIsDnsGenerated(false);
            setSelectedInstances([]);

            onCreated();
        } catch (error: any) {
            console.error("Error creating LB:", error);
            // The create route refuses a bad name with its reason (not a domain, an unverified custom domain, a name another
            // account holds, a shared-domain name with more than one label: ROADMAP 63(d)); showToast unwraps the envelope.
            showToast(error?.response?.data?.error ?? 'Failed to create load balancer', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Create Load Balancer" size="md">
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="create-form">
                <div className="form-group">
                    <label>Name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="My Load Balancer"
                        autoFocus
                    />
                </div>
                
                <div className="form-group">
                    <label>Port (Default 80)</label>
                    <input
                        type="number"
                        value={port}
                        onChange={(e) => setPort(parseInt(e.target.value))}
                    />
                </div>

                <div className="form-group">
                    <label>DNS Name (Leave empty for random generation)</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                            type="text"
                            value={dnsName}
                            onChange={(e) => {
                                setDnsName(e.target.value);
                                setIsDnsGenerated(false);
                            }}
                            placeholder="e.g., lb.mydomain.com"
                            style={{ flex: 1 }}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                setDnsName(generateDnsName());
                                setIsDnsGenerated(true);
                            }}
                            className="btn btn-primary"
                            title="Generate a random .redu.cloud domain"
                            style={{ whiteSpace: 'nowrap', height: '44px' }}
                        >
                            ⟳ Generate
                        </button>
                    </div>
                </div>

                <div className="form-group">
                    <label>Select Instances (Members)</label>
                    <Select
                        isMulti
                        options={instanceOptions}
                        value={selectedInstances}
                        onChange={(selected) => setSelectedInstances(selected as any)}
                        placeholder="Select instances..."
                        styles={getSelectStyles()}
                    />
                </div>

                <div className="modal-actions">
                    <button 
                        type="button"
                        className="cancel-button" 
                        onClick={onClose} 
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit"
                        className="submit-button" 
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Creating...' : 'Create'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default CreateLoadBalancerModal;
